
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Wallet, Link, Cloud, Database, Bot, ExternalLink, RefreshCw, BarChart } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useState, useEffect } from "react";
import { formatDate } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { ensureValidToken } from "@/services/AuthAwareApiService";

interface StatusProvider {
  name: string;
  icon: React.ReactNode;
  description: string;
  statusUrl: string;
  status: "operational" | "degraded" | "outage" | "unknown";
  lastChecked: string;
}

export function ProviderStatus() {
  const [providers, setProviders] = useState<StatusProvider[]>([
    {
      name: "Wealthica",
      icon: <Wallet className="h-5 w-5 text-blue-500" />,
      description: "Investment account connectivity and data provider",
      statusUrl: "https://wealthica.freshstatus.io/",
      status: "unknown",
      lastChecked: new Date().toISOString()
    },
    {
      name: "Vezgo",
      icon: <Link className="h-5 w-5 text-green-500" />,
      description: "Cryptocurrency account connectivity provider",
      statusUrl: "https://vezgo.freshstatus.io/",
      status: "unknown",
      lastChecked: new Date().toISOString()
    },
    {
      name: "SnapTrade",
      icon: <BarChart className="h-5 w-5 text-purple-500" />,
      description: "Investment account connectivity and data provider",
      statusUrl: "https://api.snaptrade.com/api/v1/",
      status: "unknown",
      lastChecked: new Date().toISOString()
    },
    {
      name: "Cloudflare",
      icon: <Cloud className="h-5 w-5 text-orange-500" />,
      description: "CDN and network infrastructure provider",
      statusUrl: "https://www.cloudflarestatus.com/",
      status: "unknown",
      lastChecked: new Date().toISOString()
    },
    {
      name: "Supabase",
      icon: <Database className="h-5 w-5 text-emerald-500" />,
      description: "Database and authentication provider",
      statusUrl: "https://status.supabase.com/",
      status: "unknown",
      lastChecked: new Date().toISOString()
    },
    {
      name: "Anthropic",
      icon: <Bot className="h-5 w-5 text-purple-500" />,
      description: "AI provider for analysis and insights",
      statusUrl: "https://status.anthropic.com/",
      status: "unknown",
      lastChecked: new Date().toISOString()
    },
  ]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  useEffect(() => {
    fetchProviderStatus();
  }, []);

  const fetchProviderStatus = async () => {
    try {
      setIsRefreshing(true);
      
      // Ensure valid token before making API call
      await ensureValidToken('check-provider-status');
      
      const { data, error } = await supabase.functions.invoke('check-provider-status');
      
      if (error) {
        console.error('Error fetching provider status:', error);
        throw error;
      }
      
      if (data) {
        // Update the providers state with the fetched data
        setProviders(prev => 
          prev.map(provider => {
            const match = data.find((p: any) => p.name === provider.name);
            if (match) {
              return {
                ...provider,
                status: match.status,
                lastChecked: match.lastChecked
              };
            }
            return provider;
          })
        );
      }
    } catch (error) {
      console.error('Failed to fetch provider status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusBadge = (status: StatusProvider["status"]) => {
    switch (status) {
      case "operational":
        return <Badge className="bg-green-500">Operational</Badge>;
      case "degraded":
        return <Badge className="bg-yellow-500">Degraded</Badge>;
      case "outage":
        return <Badge variant="destructive">Outage</Badge>;
      case "unknown":
      default:
        return <Badge className="bg-gray-500">Unknown</Badge>;
    }
  };

  const toggleItem = (value: string) => {
    setExpandedItems(current => 
      current.includes(value) 
        ? current.filter(item => item !== value)
        : [...current, value]
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Service Provider Status</CardTitle>
            <CardDescription>
              Monitor the status of our service providers
            </CardDescription>
          </div>
          <Button 
            onClick={fetchProviderStatus} 
            disabled={isRefreshing}
            size="sm"
            variant="outline"
          >
            {isRefreshing ? (
              <Spinner size="sm" className="mr-2" ariaLabel="Refreshing" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" value={expandedItems} onValueChange={setExpandedItems} className="w-full">
            {providers.map((provider) => (
              <AccordionItem key={provider.name} value={provider.name}>
                <div className="flex items-center justify-between">
                  <AccordionTrigger 
                    onClick={(e) => {
                      e.preventDefault();
                      toggleItem(provider.name);
                    }}
                    className="py-4"
                  >
                    <div className="flex items-center gap-2">
                      {provider.icon}
                      <span>{provider.name}</span>
                    </div>
                  </AccordionTrigger>
                  <div className="flex items-center gap-2 mr-4">
                    {getStatusBadge(provider.status)}
                  </div>
                </div>
                <AccordionContent>
                  <div className="pt-2 pb-4 space-y-4">
                    <p className="text-sm text-muted-foreground">{provider.description}</p>
                    
                    <div className="flex flex-col gap-2">
                      <div className="text-sm">
                        <span className="font-medium">Status:</span> {provider.status || "Unknown"}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Last checked:</span> {formatDate(provider.lastChecked || new Date().toISOString())}
                      </div>
                    </div>
                    
                    <div className="relative w-full h-96 border rounded-md overflow-hidden">
                      <iframe 
                        src={provider.statusUrl} 
                        className="absolute top-0 left-0 w-full h-full"
                        title={`${provider.name} Status Page`}
                        sandbox="allow-same-origin allow-scripts"
                      />
                    </div>
                    
                    <Button variant="outline" size="sm" asChild>
                      <a 
                        href={provider.statusUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Full Status Page
                      </a>
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
